import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './features/auth/AuthContext';
import { A11yEffect } from './features/auth/A11yEffect';
import { LiveAnnouncerProvider } from './components/LiveAnnouncer';
import './styles/index.css';

// Aggiornamenti PWA senza attese: quando il nuovo service worker prende il
// controllo (deploy appena uscito), ricarica la pagina così tutti vedono
// subito l'ultima versione — MA mai mentre si sta compilando la schedina.
declare const __BUILD_ID__: string;

if ('serviceWorker' in navigator) {
  let reloaded = false;
  // ATTENZIONE: alla PRIMA visita in assoluto il SW appena installato fa
  // clients.claim() e controllerchange scatta anche SENZA alcun deploy nuovo.
  // Ricaricare lì spazzava via qualunque form a metà (es. la registrazione!).
  // Si ricarica solo se la pagina AVEVA già un controller (vero aggiornamento).
  let hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true; // prima presa di controllo: la pagina è già attuale
      return;
    }
    if (reloaded) return;
    if (window.location.pathname.endsWith('/pronostici')) return;
    reloaded = true;
    window.location.reload();
  });
}

// Controllo versione ATTIVO: anche con l'app installata e tenuta aperta,
// ogni 5 minuti (e a ogni ritorno in primo piano) confrontiamo il buildId
// col server. Se è uscito un deploy: aggiorna il service worker → il
// controllerchange qui sopra ricarica da solo. Senza SW, ricarica diretta.
{
  let updating = false;
  const checkForUpdate = async () => {
    if (updating || window.location.pathname.endsWith('/pronostici')) return;
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const { buildId } = (await res.json()) as { buildId?: string };
      if (!buildId || buildId === __BUILD_ID__) return;
      updating = true;
      const registration = await navigator.serviceWorker?.getRegistration();
      if (registration) {
        await registration.update(); // scarica il nuovo SW → skipWaiting → reload
        // Rete di sicurezza: se il SW non prende il controllo entro 15s, ricarica
        // (mai dentro la schedina).
        setTimeout(() => {
          if (!window.location.pathname.endsWith('/pronostici')) window.location.reload();
        }, 15_000);
      } else {
        window.location.reload();
      }
    } catch {
      // offline o errore momentaneo: si riproverà al prossimo giro
    }
  };
  setInterval(checkForUpdate, 5 * 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LiveAnnouncerProvider>
          <AuthProvider>
            <A11yEffect />
            <App />
          </AuthProvider>
        </LiveAnnouncerProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
